import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { supabase } from 'Services/supabaseClient'
import { TABLE_CONST } from 'Utils/Constants'
import LayoutContainer from 'Components/LayoutComponent'
import Loader from 'Components/Loader'
import { DialogModal } from 'Components/UIComponents'
import DialogUIModal from 'Components/UIComponents/DialogUIModal'

const URLApproval = ({ setLoading }) => {
  const { t } = useTranslation()
  let rowCount = 8
  let paginationButtons = TABLE_CONST.paginationButtonCount

  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [adminUsersList, setAdminUsersList] = useState([])
  const [pendingUrlMessages, setPendingUrlMessages] = useState([])
  const [userData, setUserData] = useState(null)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [openDialogModal, setOpenDialogModal] = useState(false)
  const [messageStatus, setMessageStatus] = useState(null)
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {} })

  useEffect(() => {
    fetchAdminUsers()

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const fetchAdminUsers = async () => {
    setLoading(true)
    handleDismiss()

    try {
      const { data, error } = await supabase.rpc('get_admin_users')

      setAdminUsersList(data)
      if (data && data.length > 0) {
        setUserData(data[0])

        fetchPendingMessages(data[0].userid)
      }

      if (error) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('NO_DATA_FOUND'),
          onRetry: () => fetchAdminUsers(),
          onHide: handleDismiss
        })
      }
    } catch (err) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('NO_DATA_FOUND'),
        onRetry: () => fetchAdminUsers(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchPendingMessages = async (userid) => {
    handleDismiss()
    setLoading(true)
    try {
      const { data, error } = await supabase.rpc('get_pending_url_messages', { p_admin_user_id: userid })

      setPendingUrlMessages(data?.messages)
      if (error) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('NO_DATA_FOUND'),
          onRetry: () => fetchPendingMessages(userid),
          onHide: handleDismiss
        })
      }
    } catch (err) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('NO_DATA_FOUND'),
        onRetry: () => fetchPendingMessages(userid),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }
  const fetchUrlStatusMessage = async (rowData) => {
    const messageId = rowData.id
    const userid = userData.userid

    handleDismiss()
    setLoading(true)
    try {
      const { data, error } = await supabase.rpc('get_message_approved_details', { p_message_id: messageId, p_admin_user_id: userid })

      if (data) {
        setMessageStatus(data.action)
      }

      if (error) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('NO_STATUS_FOUND'),
          onRetry: () => fetchUrlStatusMessage(rowData),
          onHide: handleDismiss
        })
      }
    } catch (err) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('NO_STATUS_FOUND'),
        onRetry: () => fetchUrlStatusMessage(rowData),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const extractUrl = (text) => {
    if (!text) return []
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const matches = text.match(urlRegex)

    return matches || []
  }

  const messageBodyTemplate = (rowData) => {
    let urls = extractUrl(rowData?.content)

    if (urls.length === 0 && rowData?.attachments?.length > 0) {
      urls = extractUrl(rowData?.attachments[0]?.caption)
    }

    if (!urls || urls.length === 0) {
      return <span>{t('NO_URL_FOUND')}</span>
    }

    return (
      <ul className="url-list">
        {urls.map((url, index) => (
          <li key={index}>
            <a href={url} target="_blank" rel="noopener noreferrer" className="url-link">
              {url}
            </a>
          </li>
        ))}
      </ul>
    )
  }

  const handleWindowResize = () => {
    setWindowSize(window.innerWidth)
  }

  if (windowSize < TABLE_CONST.maxWidthTab) {
    rowCount = TABLE_CONST.mobileRowCount
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  const handleApprove = async () => {
    if (!selectedMessage || !userData) return

    handleDismiss()
    setLoading(true)
    try {
      const { status, error } = await supabase.rpc('approve_message_url', {
        p_message_id: selectedMessage.id,
        p_admin_user_id: userData.userid
      })

      if (status === 200 || status === 201) {
        setOpenDialogModal(false)
        setShowDialog({
          showModal: true,
          modalType: 'success',
          message: t('URL_APPROVED_SUCCESS'),
          onSuccess: async () => {
            handleDismiss()
            await fetchPendingMessages(userData.userid)
          },
          onHide: handleDismiss
        })
      }
      if (error) {
        setOpenDialogModal(true)
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_APPROVE_URL'),
          onRetry: () => handleApprove(),
          onHide: handleDismiss
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReject = async () => {
    if (!selectedMessage || !userData) return

    handleDismiss()
    setLoading(true)
    try {
      const { status, error } = await supabase.rpc('reject_message_url', {
        p_message_id: selectedMessage.id,
        p_admin_user_id: userData.userid
      })

      if (status === 200 || status === 201) {
        setOpenDialogModal(false)
        setShowDialog({
          showModal: true,
          modalType: 'success',
          message: t('URL_REJECTED_SUCCESS'),
          onSuccess: async () => {
            handleDismiss()
            await fetchPendingMessages(userData.userid)
          },
          onHide: handleDismiss
        })
      }

      if (error) {
        setOpenDialogModal(true)
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_REJECT_URL'),
          onRetry: () => handleReject(),
          onHide: handleDismiss
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDropdownChange = (value) => {
    setUserData(value)
    fetchPendingMessages(value?.userid)
  }

  const handleRowClick = (rowData) => {
    setSelectedMessage(rowData)
    setOpenDialogModal(true)
    fetchUrlStatusMessage(rowData)
  }

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }
  const handleCloseModal = () => {
    setOpenDialogModal(false)
  }

  const renderUrlData = () => {
    if (!selectedMessage) return null

    const urlsFromContent = extractUrl(selectedMessage.content)
    const urlsFromAttachment = extractUrl(selectedMessage?.attachments?.[0]?.caption)
    const urls = urlsFromContent.length ? urlsFromContent : urlsFromAttachment

    return (
      <div className="modal-card-wrapper">
        <div className="modal-card">
          <div className="card-header">
            <h3>{selectedMessage.community_name}</h3>
            <p className="user-name">
              By: {selectedMessage.user_first_name} {selectedMessage.user_last_name}
            </p>
          </div>

          <div className="card-body">
            <p className="message-label">
              <strong>Message:</strong>
            </p>
            <p className="message-content">{selectedMessage.content || selectedMessage?.attachments?.[0]?.caption}</p>

            {urls.length > 0 && (
              <>
                <p className="url-label">
                  <strong>URLs:</strong>
                </p>
                <ul className="url-list">
                  {urls.map((url, i) => (
                    <li key={i}>
                      <a href={url} target="_blank" rel="noopener noreferrer" className="url-link">
                        {url}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="card-footer">
            <span className="approve-count">Approvals: {selectedMessage.approval_count}</span>
            <span className="reject-count">Rejections: {selectedMessage.rejection_count}</span>
          </div>
        </div>
      </div>
    )
  }

  const renderFooter = () => (
    <div className="approval-modal-footer-btns">
      <Button label={messageStatus === 'REJECT' ? t('REJECTED') : t('REJECT')} icon="pi pi-times" className="p-button-danger" onClick={handleReject} disabled={messageStatus === 'REJECT'} />
      <Button label={messageStatus === 'APPROVE' ? t('APPROVED') : t('APPROVE')} icon="pi pi-check" className="p-button-success" onClick={handleApprove} disabled={messageStatus === 'APPROVE'} />
    </div>
  )

  const customActionButton = (rowData) => (
    <div className="url-action-button-wrapper">
      <Button type="button" icon="pi pi-cog" className="url-action-add-button" tooltip={t('APPROVE_URL')} tooltipOptions={{ position: 'top' }} onClick={() => handleRowClick(rowData)} />
    </div>
  )

  const columns = [
    {
      dataField: 'community_name',
      text: t('COMMUNITY_NAME'),
      className: 'community-name-column',
      sortable: false
    },
    {
      dataField: 'content',
      text: t('MESSAGE'),
      className: 'message-column',
      sortable: false,
      body: messageBodyTemplate
    },

    {
      dataField: 'user_first_name',
      text: t('USER_NAME'),
      className: 'user-name-column',
      sortable: false
    },
    {
      dataField: 'approved_status',
      text: t('APPROVAL_STATUS'),
      className: 'status-column',
      sortable: false
    },
    {
      dataField: 'approval_count',
      text: t('APPROVAL_COUNT'),
      className: 'count-column',
      sortable: false
    },
    {
      dataField: 'rejection_count',
      text: t('REJECTION_COUNT'),
      className: 'rejected-count-column',
      sortable: false
    },
    {
      text: t('ACTIONS'),
      className: 'column-actions',
      body: customActionButton
    }
  ]

  return (
    <>
      <LayoutContainer title={t('URL_LIST')} breadCrumbsTitle={'urlList'}>
        <div className="url-list-wrapper">
          <div className="url-list-controls">
            <Dropdown value={userData} options={adminUsersList} optionLabel="firstname" filter onChange={(e) => handleDropdownChange(e.value)} className="user-dropdown" placeholder="Select User" />
          </div>
          <div className="url-list-table">
            <DataTable value={pendingUrlMessages} paginator={pendingUrlMessages?.length} rows={rowCount} pageLinkSize={paginationButtons}>
              {columns?.map((column, index) => (
                <Column key={index} field={column.dataField} header={column.text} body={column.body} className={column.className} sortable={column.sortable} />
              ))}
            </DataTable>
          </div>
          <DialogModal {...showDialog} onDismiss={handleDismiss} />
          <DialogUIModal visible={openDialogModal} header={'URL Approval'} onHide={handleCloseModal} body={renderUrlData} footer={renderFooter} className="url-approval-modal" size="md" maximizable={false} />
        </div>
      </LayoutContainer>
    </>
  )
}

export default Loader(URLApproval)
